// P2-06: hand an idea to the user's own Claude. The link only prefills a prompt (the user
// presses send); Claude then reads the idea through the NeuralRepo connector. The idea body is
// never put in the URL: it is user content and could exceed the ~14k-char `q` limit.

export type ClaudeAction = 'triage' | 'spec' | 'synthesize';
export type ClaudeTarget = 'web' | 'desktop';

export const CLAUDE_ACTIONS: ReadonlyArray<{ action: ClaudeAction; label: string; hint: string }> = [
  { action: 'triage', label: 'Triage', hint: 'Duplicate check, next step and status' },
  { action: 'spec', label: 'Draft spec', hint: 'Problem, scope, approach, first milestone' },
  { action: 'synthesize', label: 'Synthesize', hint: 'Combine with related ideas' },
];

/** Documented limit on the prefilled prompt (support.claude.com). */
export const MAX_PROMPT_CHARS = 14000;
const MAX_TITLE_CHARS = 120;
const CONNECTOR_URL = 'https://neuralrepo.com/mcp/v2';

export interface IdeaRef {
  id: number;
  number: number;
  title: string;
}

function cleanTitle(title: string): string {
  const oneLine = title.replace(/\s+/g, ' ').trim();
  return oneLine.length > MAX_TITLE_CHARS ? `${oneLine.slice(0, MAX_TITLE_CHARS - 1)}…` : oneLine;
}

const TASKS: Record<ClaudeAction, string> = {
  triage:
    'Help me triage it: does it duplicate or overlap any related idea, what is the single next ' +
    'concrete step, and which status fits (captured, exploring, building, shipped or shelved)?',
  spec:
    'Draft a short spec for it: problem, who it is for, scope (in and out), approach, open ' +
    'questions and a first milestone. Use its related ideas where they help.',
  synthesize:
    'Synthesize it with its related ideas: the common threads, where they conflict, and one ' +
    'combined direction worth pursuing.',
};

export function buildClaudePrompt(action: ClaudeAction, idea: IdeaRef): string {
  const prompt = [
    `Use my NeuralRepo connector to read idea #${idea.number} "${cleanTitle(idea.title)}" ` +
      `(call get_idea with id ${idea.id}, including its related ideas).`,
    TASKS[action],
    'Ask me before changing anything in NeuralRepo.',
    `If the NeuralRepo connector isn't connected, tell me how to add it: ${CONNECTOR_URL}`,
  ].join('\n\n');
  return prompt.slice(0, MAX_PROMPT_CHARS);
}

/** The whole weekly review, for "Triage with Claude" on the Triage screen (P3-07). */
export function buildTriagePrompt(): string {
  return [
    'Use my NeuralRepo connector to run my weekly triage: call get_review_queue, then go through ' +
      'possible duplicates, ideas that keep coming back, ideas that have gone quiet and new connections with me, one at a time.',
    'For each, suggest what to do (merge, keep both, start exploring, shelve, keep or remove the link) ' +
      'and ask me before changing anything in NeuralRepo.',
    `If the NeuralRepo connector isn't connected, tell me how to add it: ${CONNECTOR_URL}`,
  ].join('\n\n');
}

export function claudeUrl(prompt: string, target: ClaudeTarget): string {
  const base = target === 'desktop' ? 'claude://claude.ai/new' : 'https://claude.ai/new';
  return `${base}?q=${encodeURIComponent(prompt)}`;
}

const TARGET_KEY = 'nrepo:open-in-claude-target';

export function loadClaudeTarget(): ClaudeTarget {
  try {
    return localStorage.getItem(TARGET_KEY) === 'desktop' ? 'desktop' : 'web';
  } catch {
    return 'web';
  }
}

export function saveClaudeTarget(target: ClaudeTarget): void {
  try {
    localStorage.setItem(TARGET_KEY, target);
  } catch { /* ignore */ }
}
